<template>
  <el-dialog
    :model-value="modelValue"
    class="employee-profile-dialog"
    width="980px"
    top="3vh"
    append-to-body
    :close-on-click-modal="false"
    :show-close="true"
    @close="handleClose"
  >
    <div class="dialog-shell">
      <aside class="dialog-hero">
        <p class="hero-eyebrow">Employee Portal</p>
        <h2>Complete your service profile</h2>
        <p class="hero-copy">
          This form helps BGIR establish your role, expertise, availability, and compensation details for internal service coordination.
        </p>

        <div class="progress-card">
          <span class="progress-label">Progress</span>
          <strong>{{ activeSection }} / {{ totalSections }}</strong>
          <el-progress :percentage="progressPercentage" :stroke-width="10" :show-text="false" color="#0b6cff" />
        </div>
      </aside>

      <section class="dialog-main">
        <el-form ref="formRef" :model="form" label-position="top" class="profile-form" @submit.prevent>
          <!-- Section 1: Personal Information -->
          <section v-show="activeSection === 1" class="step-panel">
            <div class="section-card">
              <div class="grid two-up">
                <el-form-item label="First Name *" :error="errors.firstName">
                  <el-input v-model="form.firstName" maxlength="80" placeholder="Your first name" />
                </el-form-item>
                <el-form-item label="Last Name *" :error="errors.lastName">
                  <el-input v-model="form.lastName" maxlength="80" placeholder="Your last name" />
                </el-form-item>
              </div>
              <div class="grid two-up">
                <el-form-item label="Email Address *" :error="errors.email">
                  <el-input v-model="form.email" type="email" maxlength="160" placeholder="your@email.com" />
                </el-form-item>
                <el-form-item label="Primary Contact Number *" :error="errors.primaryContactNumber">
                  <el-input v-model="form.primaryContactNumber" maxlength="30" placeholder="+1 (555) 123-4567" />
                </el-form-item>
              </div>
              <el-form-item label="Country of Citizenship *" :error="errors.countryOfCitizenship">
                <el-input v-model="form.countryOfCitizenship" maxlength="100" placeholder="Country name" />
              </el-form-item>
            </div>
          </section>

          <!-- Section 2: Department Alignment -->
          <section v-show="activeSection === 2" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="Department Alignment *"
                description="Select the department(s) where your expertise aligns"
                :error="errors.departments"
              >
                <el-checkbox-group v-model="form.departments" class="checkbox-grid">
                  <el-checkbox v-for="dept in departmentOptions" :key="dept" :label="dept">
                    {{ dept }}
                  </el-checkbox>
                </el-checkbox-group>
              </el-form-item>
              <el-form-item
                v-if="form.departments.includes('Other')"
                label="Please specify the other department(s) *"
                :error="errors.departmentsOther"
              >
                <el-input v-model="form.departmentsOther" type="textarea" :rows="2" maxlength="500" />
              </el-form-item>
            </div>
          </section>

          <!-- Section 3: Service Categories -->
          <section v-show="activeSection === 3" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="Service Categories *"
                description="Select the services that align with your skill set"
                :error="errors.serviceCategories"
              >
                <el-checkbox-group v-model="form.serviceCategories" class="checkbox-grid">
                  <el-checkbox v-for="service in serviceOptions" :key="service" :label="service">
                    {{ service }}
                  </el-checkbox>
                </el-checkbox-group>
              </el-form-item>
              <el-form-item
                v-if="form.serviceCategories.includes('Other')"
                label="Please specify the other service(s) *"
                :error="errors.serviceCategoriesOther"
              >
                <el-input v-model="form.serviceCategoriesOther" type="textarea" :rows="2" maxlength="500" />
              </el-form-item>
            </div>
          </section>

          <!-- Section 4: Weekly Capacity -->
          <section v-show="activeSection === 4" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="Weekly Capacity *"
                description="Your estimated weekly availability for service assignments"
                :error="errors.weeklyCapacity"
              >
                <el-radio-group v-model="form.weeklyCapacity" class="option-group">
                  <el-radio label="low">Low (1 to 5 hours/week)</el-radio>
                  <el-radio label="moderate">Moderate (6 to 15 hours/week)</el-radio>
                  <el-radio label="high">High (15+ hours/week)</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </section>

          <!-- Section 5: Compensation -->
          <section v-show="activeSection === 5" class="step-panel">
            <div class="section-card">
              <div class="disclaimer-box">
                <p class="disclaimer-title">Compensation Terms & Rate Verification</p>
                <p>
                  By providing your rate specifications, you acknowledge that all compensation is subject to prior administrative approval and must align with
                  established BGIR budgetary allocations and project-specific funding.
                </p>
                <p>
                  Final compensation rates will be confirmed in writing via a formal Service Agreement or internal memorandum before project commencement.
                  Payments are processed based on verified deliverables and accurately logged hours.
                </p>
              </div>

              <div class="grid two-up">
                <el-form-item label="Preferred Rate Type *" :error="errors.rateType">
                  <el-radio-group v-model="form.rateType" class="option-group">
                    <el-radio label="hourly">Hourly</el-radio>
                    <el-radio label="per_project">Per Project/Service</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item
                  :label="`What is your established rate? (${form.rateType === 'hourly' ? 'per hour' : 'per project/service'}) *`"
                  :error="errors.rate"
                >
                  <el-input v-model="form.rate" type="number" placeholder="0.00" min="0" step="0.01" />
                </el-form-item>
              </div>
            </div>
          </section>

          <!-- Section 6: Compliance -->
          <section v-show="activeSection === 6" class="step-panel">
            <div class="section-card">
              <div class="compliance-box">
                <p class="compliance-title">Internal Attestation & Data Governance</p>
                <p>
                  By submitting this internal request, you acknowledge that all data handled during this collaboration remains the intellectual property of BGIR and
                  is subject to our standard confidentiality and non-disclosure protocols.
                </p>
                <p>
                  Internal resource allocation is based on current staff bandwidth and project priority. Submission of this form does not guarantee immediate
                  commencement; your supervisor will be notified of this request to ensure alignment with your current billable hours and services requested goals.
                </p>
              </div>

              <el-form-item :error="errors.acknowledgment">
                <el-checkbox v-model="form.acknowledgment" true-label="agree" false-label="">
                  I acknowledge these terms and wish to proceed.
                </el-checkbox>
              </el-form-item>
            </div>
          </section>
        </el-form>

        <div class="dialog-actions">
          <el-button v-if="activeSection > 1" @click="previousSection">
            Back
          </el-button>
          <el-button @click="handleClose">
            Close
          </el-button>
          <el-button
            v-if="activeSection < totalSections"
            type="primary"
            @click="nextSection"
          >
            Continue
          </el-button>
          <el-button
            v-else
            type="success"
            :loading="isSubmitting"
            :disabled="form.acknowledgment !== 'agree'"
            @click="handleSubmit"
          >
            Submit profile
          </el-button>
        </div>
      </section>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { auth, db, serverTimestamp } from '@/firebase'
import { doc, setDoc } from 'firebase/firestore'
import { buildEmployeeProfileDraft } from '@/services/userProfile'

const DEPARTMENT_OPTIONS = [
  'Manuscript Review & Scientific Editing',
  'Internal Quality Assurance & ACGME Audit',
  'Conference/Symposium Support',
  'Internal Grant Peer-Review',
  'Cross-Departmental Research Collaboration',
  'Advanced Statistical Modeling & Data Validation',
  'Institutional IRB Liaison Support',
  'Curriculum/Lecture Material Development',
  'Standardized Reporting & Impact Evaluation',
  'Internal Training & Mentorship Programs',
  'Other',
]

const SERVICE_OPTIONS = [
  'ACGME Accreditation Guidance',
  'Scientific Writing',
  'Conference Preparation',
  'Poster & Presentation Design',
  'Grant Preparation',
  'Research Collaboration',
  'Statistical Services',
  'Project Management',
  'Personalized Mentorship',
  'Clinical & Basic Science Support',
  'IRB Submission Support',
  'Streamlined Research Documentation',
  'Webinar & Lecture Development',
  'Research Evaluation & Reporting',
  'Data Handling & Analysis',
  'Event Planning & Recognition',
  'Other',
]

const TOTAL_SECTIONS = 6

export default {
  name: 'EmployeeProfileForm',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    userProfile: {
      type: Object,
      default: null,
    },
  },
  emits: ['form-submitted', 'form-closed'],
  data() {
    return {
      activeSection: 1,
      totalSections: TOTAL_SECTIONS,
      departmentOptions: DEPARTMENT_OPTIONS,
      serviceOptions: SERVICE_OPTIONS,
      form: buildEmployeeProfileDraft(auth.currentUser, this.userProfile),
      errors: {},
      isSubmitting: false,
      autoSaveTimer: null,
    }
  },
  computed: {
    progressPercentage() {
      return Math.round((this.activeSection / this.totalSections) * 100)
    },
  },
  watch: {
    userProfile: {
      immediate: true,
      handler(profile) {
        this.form = buildEmployeeProfileDraft(auth.currentUser, profile)
      },
    },
    modelValue(isOpen) {
      if (!isOpen) return
      this.activeSection = 1
      this.errors = {}
      this.form = buildEmployeeProfileDraft(auth.currentUser, this.userProfile)
    },
    form: {
      deep: true,
      handler() {
        if (!this.modelValue || this.isSubmitting) return
        this.queueAutoSave()
      },
    },
  },
  beforeUnmount() {
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
  },
  methods: {
    buildPersistedProfile(statusOverride = null) {
      const departments = Array.isArray(this.form.departments) ? [...this.form.departments] : []
      const serviceCategories = Array.isArray(this.form.serviceCategories) ? [...this.form.serviceCategories] : []
      const hasDepartmentOther = departments.includes('Other')
      const hasServiceOther = serviceCategories.includes('Other')

      return {
        ...this.form,
        firstName: (this.form.firstName || '').trim(),
        lastName: (this.form.lastName || '').trim(),
        email: (this.form.email || '').trim(),
        primaryContactNumber: (this.form.primaryContactNumber || '').trim(),
        countryOfCitizenship: (this.form.countryOfCitizenship || '').trim(),
        departments,
        departmentsOther: hasDepartmentOther ? (this.form.departmentsOther || '').trim() : '',
        serviceCategories,
        serviceCategoriesOther: hasServiceOther ? (this.form.serviceCategoriesOther || '').trim() : '',
        rate: this.form.rate == null ? '' : String(this.form.rate).trim(),
        status: statusOverride || this.form.status || 'draft',
      }
    },
    queueAutoSave() {
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
      this.autoSaveTimer = setTimeout(() => {
        this.persistDraft()
      }, 700)
    },
    async persistDraft() {
      const user = auth.currentUser
      if (!user?.uid) return

      const currentStatus = this.form.status === 'completed' ? 'completed' : 'draft'
      const profilePayload = this.buildPersistedProfile(currentStatus)
      const docRef = doc(db, 'users', user.uid)
      await setDoc(
        docRef,
        {
          employeeProfile: {
            ...profilePayload,
            updatedAt: serverTimestamp(),
          },
        },
        { merge: true }
      )
    },
    validateSectionFields(section) {
      this.errors = {}

      if (section === 1) {
        if (!this.form.firstName?.trim()) this.errors.firstName = 'First name is required.'
        if (!this.form.lastName?.trim()) this.errors.lastName = 'Last name is required.'
        if (!this.form.email?.trim()) this.errors.email = 'Email is required.'
        if (!this.form.primaryContactNumber?.trim()) this.errors.primaryContactNumber = 'Contact number is required.'
        if (!this.form.countryOfCitizenship?.trim()) this.errors.countryOfCitizenship = 'Country is required.'
      }

      if (section === 2) {
        if (!this.form.departments?.length) this.errors.departments = 'Select at least one department.'
        if (this.form.departments.includes('Other') && !this.form.departmentsOther?.trim()) {
          this.errors.departmentsOther = 'Please specify other department(s).'
        }
      }

      if (section === 3) {
        if (!this.form.serviceCategories?.length) this.errors.serviceCategories = 'Select at least one service category.'
        if (this.form.serviceCategories.includes('Other') && !this.form.serviceCategoriesOther?.trim()) {
          this.errors.serviceCategoriesOther = 'Please specify other service category/categories.'
        }
      }

      if (section === 4) {
        if (!this.form.weeklyCapacity) this.errors.weeklyCapacity = 'Weekly capacity is required.'
      }

      if (section === 5) {
        if (!this.form.rateType) this.errors.rateType = 'Rate type is required.'
        if (!this.form.rate || parseFloat(this.form.rate) < 0) this.errors.rate = 'Valid rate is required.'
      }

      if (section === 6) {
        if (this.form.acknowledgment !== 'agree') this.errors.acknowledgment = 'You must acknowledge the terms to proceed.'
      }

      return Object.keys(this.errors).length === 0
    },
    nextSection() {
      if (this.validateSectionFields(this.activeSection) && this.activeSection < this.totalSections) {
        this.activeSection++
      }
    },
    previousSection() {
      if (this.activeSection > 1) {
        this.activeSection--
      }
    },
    async handleSubmit() {
      try {
        if (!this.validateSectionFields(this.totalSections)) {
          ElMessage({ type: 'error', message: 'Please complete all required fields.' })
          return
        }

        if (this.form.acknowledgment !== 'agree') {
          ElMessage({ type: 'error', message: 'You must acknowledge the terms to proceed.' })
          return
        }

        this.isSubmitting = true

        const user = auth.currentUser
        if (!user) throw new Error('Not authenticated')

        const docRef = doc(db, 'users', user.uid)
        const profilePayload = this.buildPersistedProfile('completed')
        await setDoc(
          docRef,
          {
            employeeProfile: {
              ...profilePayload,
              completedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
          },
          { merge: true }
        )

        this.form = {
          ...this.form,
          status: 'completed',
        }
        ElMessage({ type: 'success', message: 'Profile submitted successfully!' })
        this.$emit('form-submitted', this.form)
      } catch (error) {
        ElMessage({ type: 'error', message: error?.message || 'Failed to submit profile.' })
      } finally {
        this.isSubmitting = false
      }
    },
    handleClose() {
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
      this.persistDraft().catch(() => {})
      this.$emit('form-closed')
    },
  },
}
</script>

<style scoped>
.dialog-shell {
  display: grid;
  grid-template-columns: minmax(240px, 290px) minmax(0, 1fr);
  min-height: min(82vh, 860px);
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border-radius: 28px;
  overflow: hidden;
}

.dialog-hero {
  padding: 30px 24px;
  background:
    radial-gradient(circle at top, rgba(11, 108, 255, 0.2), transparent 42%),
    linear-gradient(180deg, #003831 0%, #05564d 100%);
  color: #f8fffd;
}

.hero-eyebrow {
  margin: 0 0 8px;
  color: #9fd0ff;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dialog-hero h2 {
  margin: 0 0 10px;
  font-size: 2rem;
  line-height: 1.08;
  font-weight: 850;
}

.hero-copy {
  margin: 0 0 22px;
  color: rgba(248, 255, 253, 0.86);
}

.progress-card {
  margin-bottom: 22px;
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.progress-label {
  display: block;
  margin-bottom: 6px;
  color: rgba(248, 255, 253, 0.76);
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.progress-card strong {
  display: block;
  margin-bottom: 12px;
  font-size: 1.35rem;
}

.dialog-main {
  display: flex;
  flex-direction: column;
  padding: 28px 28px 24px;
}

.profile-form {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-right: 6px;
}

.step-panel {
  padding-bottom: 12px;
}

.section-card {
  padding: 20px;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid rgba(3, 36, 31, 0.08);
  box-shadow: 0 16px 36px rgba(3, 36, 31, 0.06);
}

.grid {
  display: grid;
  gap: 14px;
}

.two-up {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
}

.checkbox-grid :deep(.el-checkbox) {
  box-sizing: border-box;
  width: 100%;
  min-height: 64px;
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f7fbff;
  border: 1px solid rgba(11, 108, 255, 0.12);
}

.checkbox-grid :deep(.el-checkbox__label) {
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;
}

.checkbox-grid :deep(.el-checkbox:hover) {
  background: #f0f8ff;
  border-color: rgba(11, 108, 255, 0.24);
}

.option-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.option-group :deep(.el-radio) {
  margin-right: 0;
}

:deep(.el-form-item__label) {
  font-size: 1rem;
  line-height: 1.45;
  font-weight: 700;
  color: #03241f;
}

.disclaimer-box,
.compliance-box {
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff 0%, #f3f8ff 100%);
  border: 1px solid rgba(11, 108, 255, 0.12);
  margin-bottom: 18px;
}

.disclaimer-title,
.compliance-title {
  margin: 0 0 10px;
  color: #03241f;
  font-size: 0.95rem;
  font-weight: 800;
}

.disclaimer-box > p,
.compliance-box > p {
  margin: 0 0 10px;
  color: #486a64;
  font-size: 0.9rem;
  line-height: 1.5;
}

.disclaimer-box > p:last-child,
.compliance-box > p:last-child {
  margin-bottom: 0;
}

.rejection-alert {
  margin-top: 12px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

:deep(.employee-profile-dialog .el-dialog) {
  max-width: calc(100vw - 24px);
  border-radius: 28px;
  overflow: hidden;
  background: transparent;
}

:deep(.employee-profile-dialog .el-dialog__header) {
  display: none;
}

:deep(.employee-profile-dialog .el-dialog__body) {
  padding: 0;
}

@media (max-width: 900px) {
  .dialog-shell {
    grid-template-columns: 1fr;
  }

  .dialog-hero {
    padding-bottom: 18px;
  }
}

@media (max-width: 680px) {
  .dialog-main {
    padding: 18px;
  }

  .two-up,
  .checkbox-grid {
    grid-template-columns: 1fr;
  }

  .dialog-hero h2 {
    font-size: 1.6rem;
  }
}
</style>
